import React from "react";
import { ChevronRight, Clock3 } from "lucide-react";
import {
  deriveOrderMessageKey,
  deriveOrderMessageOptions,
  formatAmountRwf,
  resolveAcceptedOfferAmount,
  resolveProductImage,
} from "./ordersUtils";

export default function OrderListItem({
  offer,
  isSeller,
  productTitle,
  actionLoading,
  onAccept,
  onDecline,
  onOpen,
  partnerName,
  selfName,
  t,
}) {
  const imageSrc = resolveProductImage(offer);
  const messageKey = deriveOrderMessageKey(offer, isSeller);
  const messageOptions = deriveOrderMessageOptions({
    offer,
    isSeller,
    partnerName,
    selfName,
  });
  const acceptedAmount = resolveAcceptedOfferAmount(offer);
  const statusMessage = t(messageKey, "Order in progress", messageOptions);
  const isPendingSellerAction =
    isSeller && String(offer?.status || "").toLowerCase() === "pending";

  return (
    <div className="border-b border-gray-200 dark:border-neutral-800 last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen();
          }
        }}
        className="flex items-center gap-3 px-4 py-3.5 sm:py-3 hover:bg-gray-50 dark:hover:bg-neutral-800/40 transition-colors"
      >
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-neutral-800 shrink-0">
          <img
            src={imageSrc}
            alt={productTitle}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="min-w-0 flex-1 pr-1">
          <p className="text-[15px] sm:text-base font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2 sm:line-clamp-1">
            {productTitle}
          </p>
          <p className="text-sm sm:text-sm text-gray-700 dark:text-gray-300 mt-1">
            {formatAmountRwf(acceptedAmount)}
          </p>
          <div className="flex items-start gap-1.5 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            <Clock3 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="line-clamp-2 leading-snug">{statusMessage}</span>
          </div>
        </div>

        {isPendingSellerAction ? (
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              disabled={actionLoading === offer.id}
              onClick={(event) => {
                event.stopPropagation();
                onAccept();
              }}
              className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition"
            >
              {t("actions.accept", "Accept")}
            </button>
            <button
              type="button"
              disabled={actionLoading === offer.id}
              onClick={(event) => {
                event.stopPropagation();
                onDecline();
              }}
              className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition"
            >
              {t("actions.decline", "Decline")}
            </button>
          </div>
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 shrink-0" />
        )}
      </div>

      {isPendingSellerAction ? (
        <div className="sm:hidden px-4 pb-3 flex items-center gap-2">
          <button
            type="button"
            disabled={actionLoading === offer.id}
            onClick={onAccept}
            className="flex-1 px-3 py-2 text-xs font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition"
          >
            {t("actions.accept", "Accept")}
          </button>
          <button
            type="button"
            disabled={actionLoading === offer.id}
            onClick={onDecline}
            className="flex-1 px-3 py-2 text-xs font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition"
          >
            {t("actions.decline", "Decline")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
